'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { earnSummaryBunja } from '@flipster/features/src/shared/earn/state';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { currencyDetailsBunja } from '@flipster/socket/r230522/currency-detail';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import { UsdCurrency, UsdPrecision, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import CumulativeEarnings from './cumulative-earnings';

const EarningsOverview = () => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { currencyDetailsAtom, subscribeCurrencies } = useBunja(currencyDetailsBunja);

  const earnSummary = useAtomValue(earnSummaryAtom);
  const currencyDetail = useAtomValue(currencyDetailsAtom);

  const balanceUSDT = Num(earnSummary?.totalAppliedBalance ?? 0).toString();
  const balanceUSD =
    balanceUSDT && currencyDetail
      ? Num(balanceUSDT).dividedBy(currencyDetail.USD.usdtRate).toString()
      : Num(0).toString();
  const estimatedEarning = Num(earnSummary?.estimatedEarnings ?? 0).toString();

  const tooltipContentsForEarningProcess =
    'Earnings are paid daily around 00:00 UTC. Your USDT asset includes margins and unrealized P&L. Other assets are based on their USDT market value.';
  const tooltipContentsForAprAppliedBalance = (
    <ul className={css({ list: 'disc' })}>
      <li>Balance used for daily earnings calculation.</li>
      <li>Converted to USDT at current market prices.</li>
      <li>Includes unrealized P&L, updated on every entrance to earn page.</li>
      <li>Calculated based on earn limit for each APR category.</li>
    </ul>
  );

  useEffect(() => {
    const unsubscribe = subscribeCurrencies([UsdCurrency]);
    return () => {
      unsubscribe();
    };
  }, [subscribeCurrencies]);

  return (
    <article
      className={css({
        p: 1,
        bgGradient: 'programs.promotion',
        rounded: 8,
      })}
    >
      <div
        className={flex({
          align: 'flex-start',
          justify: 'space-between',
          p: [16, 20],
          color: 'content.default.level-4',
        })}
      >
        <div className={flex({ direction: 'column', justify: 'center', align: 'flex-start', gap: 4 })}>
          <h2 className={css({ textStyle: 'Title/S', whiteSpace: 'pre-wrap' })}>Est.earnings</h2>
          <Tooltip
            placement='bottom-start'
            maxWidth={300}
            mobileAlert={() => {
              alert.show({ title: 'Earning process', message: tooltipContentsForEarningProcess });
            }}
          >
            <Tooltip.Trigger css={{ textStyle: 'Text/S/Underline', color: 'content.default.level-4' }}>
              Earning process
            </Tooltip.Trigger>
            <Tooltip.Content>{tooltipContentsForEarningProcess}</Tooltip.Content>
          </Tooltip>
        </div>
        <div className={flex({ direction: 'column', align: 'flex-end', gap: 8 })}>
          <Formatted.Number
            value={estimatedEarning}
            precision={UsdtPrecision}
            toFixed
            css={{ textStyle: 'Title/S', color: 'content.default.level-4' }}
            suffix={<span className={css({ textStyle: 'Text/M/Regular' })}>{UsdtCurrency}</span>}
          />
          <div className={css({ display: 'flex', gap: 4, whiteSpace: 'nowrap' })}>
            <div className={badgeStyle}>Daily</div>
            <div className={badgeStyle}>All assets in USDT</div>
          </div>
        </div>
      </div>
      <div
        className={stack({
          gap: 12,
          px: { base: 16, tablet: 20 },
          pt: { base: 16, tablet: 24 },
          pb: { base: 12, tablet: 24 },
          bg: 'background.default.level-1',
          roundedBottom: 8,
        })}
      >
        <CumulativeEarnings />
        <div className={flex({ align: 'flex-start', justify: 'space-between' })}>
          <span
            className={hstack({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-4',
            })}
          >
            APR applied balance
            <Tooltip
              placement='bottom'
              maxWidth={500}
              crossAxisOffset={50}
              mobileAlert={() => {
                alert.show({
                  title: 'APR applied balance',
                  message: tooltipContentsForAprAppliedBalance,
                  confirmText: 'OK',
                });
              }}
            >
              <Tooltip.Trigger>
                <IconButton icon={<InformationCircleIcon />} size='small' hierarchy='quaternary' />
              </Tooltip.Trigger>
              <Tooltip.Content>{tooltipContentsForAprAppliedBalance}</Tooltip.Content>
            </Tooltip>
          </span>
          <p
            className={css({
              justifySelf: 'flex-end',
              textAlign: 'right',
            })}
          >
            <Formatted.Number
              value={balanceUSDT}
              precision={UsdtPrecision}
              toFixed
              css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-4' }}
              suffix={<span className={css({ textStyle: 'Text/M/Regular' })}>{UsdtCurrency}</span>}
            />
            <br />
            <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
              ≈ <Formatted.Number value={balanceUSD} precision={UsdPrecision} toFixed prefix='$' />
            </span>
          </p>
        </div>
      </div>
    </article>
  );
};

const badgeStyle = css({
  textStyle: 'Text/XS/Regular',
  color: 'content.default.level-4',
  py: 2,
  px: 8,
  borderRadius: 10,
  border: '0.5px solid {colors.border.default.level-4}',
});

export default EarningsOverview;
